import React, { useEffect, useRef, useState } from 'react';

import { ExclamationCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import {
  Avatar,
  Button,
  Dropdown,
  MenuProps,
  Modal,
  Space,
  Tooltip,
} from 'antd';
import { Building2, LogOut, Settings, User as UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Company } from 'models/Company';
import { Role } from 'models/Role';
import { User } from 'models/User';

import { AuthService } from 'services/AuthService';

import { USER_TYPES } from '../../../config/Constants';
import { useThemeLayoutConfig } from '../../../config/ThemeDashboardLayoutConfig';
import AuthAction from '../../../redux/actions/authActions/Actions';
import IntlAction from '../../../redux/actions/intlActions/Actions';
import { ImpersonationState } from '../../../redux/reducers/ImpersonationReducer';
import ConfigSettings, { ConfigSettingsHandle } from '../ConfigSettings';
import ImpersonationModal from './ImpersonationModal';
import ProfileEditModal from './ProfileEditModal';

import './ProfileMenu.scss';

interface ProfileMenuProps {
  loading: boolean;
  setLoading: (value: boolean) => void;
  companies: Company[];
  roles: Role[];
}

function ProfileMenu({
  loading,
  setLoading,
  companies,
  roles,
}: ProfileMenuProps) {
  const [modal, contextModalHolder] = Modal.useModal();
  const customConfig = useThemeLayoutConfig();
  const authUser: User = useSelector((state: any) => state.auth);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [items, setItem] = useState<MenuProps['items']>([]);
  const configSettingsRef = useRef<ConfigSettingsHandle | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isImpersonationOpen, setIsImpersonationOpen] = useState(false);
  const currentImpersonation: ImpersonationState = useSelector(
    (state: any) => state.impersonation
  );

  const openConfigDrawer = () => {
    if (
      configSettingsRef.current &&
      [USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN].includes(authUser.userType) &&
      !loading
    ) {
      configSettingsRef.current.openDrawer();
    }
  };

  const toggleLanguage = () => {
    const next = i18n.language === 'es' ? 'en' : 'es';
    localStorage.setItem('userLang', next);
    i18n.changeLanguage(next);
    dispatch(IntlAction.update({ lang: next }));
  };

  useEffect(() => {
    setItem([
      {
        key: 'header',
        label: (
          <div className="user-header">
            <div className="user-info-container">
              <Avatar
                src={authUser.avatar || undefined}
                style={{
                  backgroundColor:
                    customConfig.customValues.secondaryColor || '#cfe8fc',
                  color: customConfig.theme.token?.colorPrimary || '#0077cc',
                  cursor: 'pointer',
                }}
                size="large"
              >
                {!authUser.avatar && (
                  <span
                    style={{
                      textTransform: 'uppercase',
                    }}
                  >
                    {authUser.name.slice(0, 1) + authUser.lastname.slice(0, 1)}
                  </span>
                )}
              </Avatar>

              <div>
                <div className="user-name">{authUser.name}</div>
                <div className="user-company">{authUser.company.name}</div>
                <div className="user-email">{authUser.email}</div>
              </div>
            </div>
          </div>
        ),
        type: 'group',
      },
      {
        type: 'divider',
      },
      {
        key: 'profile',
        label: t('components.layout.profile_menu.items.profile'),
        icon: <UserIcon className="menu-icon" />,
        disabled: false,
        onClick: () => {
          if (!loading) {
            setIsProfileOpen(true);
          }
        },
      },
      ...([USER_TYPES.SUPER_ADMIN, USER_TYPES.ADMIN].includes(authUser.userType)
        ? [
            {
              key: 'settings',
              label: t('components.layout.profile_menu.items.settings'),
              icon: <Settings className="menu-icon" />,
              onClick: openConfigDrawer,
            },
            {
              key: 'impersonation',
              label: (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span>
                    {t('components.layout.profile_menu.items.change_context')}
                  </span>
                </span>
              ),
              icon: <Building2 className="menu-icon" />,
              onClick: () => {
                if (!loading) {
                  setIsImpersonationOpen(true);
                }
              },
            },
          ]
        : []),
      {
        key: 'language',
        icon: (
          <GlobalOutlined
            className="menu-icon"
            style={{ paddingLeft: '2px' }}
          />
        ),
        label: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleLanguage();
            }}
          >
            <span>{t('components.layout.language_switcher.tooltip')}</span>
            <span style={{ display: 'inline-flex', gap: 8 }}>
              <Tooltip
                title={t('components.layout.language_switcher.items.es')}
              >
                <Button
                  size="small"
                  type={i18n.language === 'es' ? 'primary' : 'default'}
                  onClick={(e) => {
                    e.stopPropagation();
                    localStorage.setItem('userLang', 'es');
                    i18n.changeLanguage('es');
                    dispatch(IntlAction.update({ lang: 'es' }));
                  }}
                >
                  ES
                </Button>
              </Tooltip>
              <Tooltip
                title={t('components.layout.language_switcher.items.en')}
              >
                <Button
                  size="small"
                  type={i18n.language === 'en' ? 'primary' : 'default'}
                  onClick={(e) => {
                    e.stopPropagation();
                    localStorage.setItem('userLang', 'en');
                    i18n.changeLanguage('en');
                    dispatch(IntlAction.update({ lang: 'en' }));
                  }}
                >
                  EN
                </Button>
              </Tooltip>
            </span>
          </div>
        ),
        // Sin onClick en el item para que no se cierre el menú
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        label: t('components.layout.profile_menu.items.logout'),
        icon: <LogOut className="menu-icon" />,
        danger: true,
        onClick: handleLogout,
      },
    ]);
  }, [authUser, loading, i18n.language, currentImpersonation]);

  const handleLogout = () => {
    modal.confirm({
      title: t('components.layout.profile_menu.logout_confirm.title'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <span>
          {t('components.layout.profile_menu.logout_confirm.content')}
        </span>
      ),
      okText: t('components.layout.profile_menu.logout_confirm.ok'),
      cancelText: t('components.layout.profile_menu.logout_confirm.cancel'),
      onOk: logout,
    });
  };

  const logout = async () => {
    setLoading(true);

    if (authUser.token) {
      await AuthService.logout();
    }

    dispatch(AuthAction.signOut({}));
    setLoading(false);
  };

  return (
    <>
      <Space direction="vertical">
        {contextModalHolder}
        <Space wrap>
          <Dropdown
            menu={{ items }}
            trigger={['click']}
            placement="bottomLeft"
            arrow
          >
            <div onClick={(e) => e.preventDefault()}>
              <Space size={16} wrap>
                <Avatar
                  src={authUser.avatar || undefined}
                  style={{
                    backgroundColor:
                      customConfig.customValues.secondaryColor || '#cfe8fc',
                    color: customConfig.theme.token?.colorPrimary || '#0077cc',
                    cursor: 'pointer',
                  }}
                >
                  {!authUser.avatar && (
                    <span
                      style={{
                        textTransform: 'uppercase',
                      }}
                    >
                      {authUser.name.slice(0, 1) +
                        authUser.lastname.slice(0, 1)}
                    </span>
                  )}
                </Avatar>
              </Space>
            </div>
          </Dropdown>
        </Space>
      </Space>

      <ConfigSettings ref={configSettingsRef} hideFloatButton />
      <ProfileEditModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />
      <ImpersonationModal
        isOpen={isImpersonationOpen}
        onClose={() => setIsImpersonationOpen(false)}
        companies={companies}
        roles={roles}
      />
    </>
  );
}

export default ProfileMenu;
